import { useCallback, useState } from 'react';

import {
  Button,
  HelpCircleFillIcon,
  Input,
  Radio,
  RadioGroup,
  RefreshIcon,
  Select,
  Text,
  TextButton,
  Textarea,
  Tooltip,
  type TypeSelectOption,
} from '@thefarmersfront/kitchen';

import { FormField, ModalSection } from '~/components/common';

import { useKakaoPostcodeSearch } from '~/hooks/common';

import {
  COLLECTING_REQUEST_BOX_COUNT_OPTIONS,
  COLLECTING_REQUEST_CONTACT_TEL_MAX_LENGTH,
  COLLECTING_REQUEST_DETAIL_MAX_LENGTH,
  COLLECTING_REQUEST_PRODUCT_NAME_MAX_LENGTH,
  COLLECTING_REQUEST_SECONDARY_ADDRESS_MAX_LENGTH,
} from '~/constants/delivery-agency';

import type useCollectingRequestRegisterForm from './useCollectingRequestRegisterForm';

import type { CollectingRequestRegisterOptions } from '~/types/delivery-agency.types';

import styles from './CollectingRequestRegisterModal.module.scss';

const TEL_TOOLTIP = "전화번호/핸드폰 번호/안심번호(택1) 를 하이픈'-' 포함해서 입력해 주세요.";
const BOX_COUNT_TOOLTIP =
  "고객이 회수 요청한 박스의 총 수량입니다.\n부정확할 경우 회수를 진행하지 않고 '회수 실패' 처리 됩니다.";
const HANDLING_TOOLTIP = 'NMHub에 상품 입고 시키는 차량에 실어서 보내드립니다.';

const findOption = <T extends string | number>(candidates: readonly TypeSelectOption<T>[], value: T | null) =>
  candidates.find((option) => option.value === value) ?? null;

/** [반품 신청 2step] 회수 정보 입력 영역 */
const CollectingRequestCollectingInfoForm = ({
  receiverName,
  options,
  form,
  onSearchAddress,
  onReset,
}: {
  receiverName: string;
  options: CollectingRequestRegisterOptions;
  form: ReturnType<typeof useCollectingRequestRegisterForm>;
  onSearchAddress: (primaryAddress: string) => void;
  onReset: () => void;
}) => {
  const { values, errors } = form;

  const [boxCountDropdownTarget, setBoxCountDropdownTarget] = useState<HTMLElement | null>(null);
  const handleBoxCountSelectRef = useCallback((element: HTMLDivElement | null) => {
    setBoxCountDropdownTarget(element?.closest<HTMLElement>('[role="dialog"]') ?? null);
  }, []);

  const { handleZipCodeSearchOpen } = useKakaoPostcodeSearch({
    onComplete: (_, address) => onSearchAddress(address),
  });

  return (
    <ModalSection title="회수 정보" className={styles['collecting-info']}>
      <TextButton
        className={styles['reset-button']}
        leftIcon={<RefreshIcon size={16} />}
        size="small"
        color="secondary"
        onClick={onReset}
      >
        회수 정보 초기화
      </TextButton>

      <FormField
        label="원주문 수령자명"
        required
        contentAlign="center"
        contentsClassName={styles['field-value']}
        contents={
          <Text size="large" element="span">
            {receiverName}
          </Text>
        }
      />
      <FormField
        label="고객 연락처"
        required
        tooltip={{ description: TEL_TOOLTIP }}
        contentAlign="center"
        contents={
          <Input
            type="text"
            size="small"
            placeholder="숫자/ 하이픈'-'만 입력 (하이픈 필수)"
            maxLength={COLLECTING_REQUEST_CONTACT_TEL_MAX_LENGTH}
            value={values.tel}
            status={errors.tel ? 'error' : 'default'}
            validationMessage={errors.tel}
            onChange={(event) => form.handleChangeTel(event.target.value)}
          />
        }
      />
      <FormField
        label="회수지 도로명 기본주소"
        required
        contentAlign="center"
        contents={
          <div className={styles['address-field']}>
            <Input
              type="text"
              size="small"
              placeholder="도로명 주소를 검색해 주세요."
              disabled
              value={values.primaryAddress}
              status={errors.primaryAddress ? 'error' : 'default'}
              validationMessage={errors.primaryAddress}
            />
            <Button size="small" variant="contained" color="secondary" onClick={handleZipCodeSearchOpen}>
              검색
            </Button>
          </div>
        }
      />
      <FormField
        label="회수지 상세주소"
        contentAlign="center"
        contents={
          <Input
            type="text"
            size="small"
            placeholder="동/호수, 층수 등을 입력해 주세요."
            count
            maxLength={COLLECTING_REQUEST_SECONDARY_ADDRESS_MAX_LENGTH}
            value={values.secondaryAddress}
            onChange={(event) => form.handleChangeDetailText('secondaryAddress', event.target.value)}
          />
        }
      />
      <FormField
        label="상품회수장소"
        required
        contentAlign="center"
        contents={
          <Select
            size="small"
            placeholder="선택"
            options={options.deliveryLocations}
            value={findOption(options.deliveryLocations, values.deliveryLocation)}
            status={errors.deliveryLocation ? 'error' : 'default'}
            validationMessage={errors.deliveryLocation}
            onChange={(_: unknown, option: TypeSelectOption<string>) => form.handleChangeDeliveryLocation(option.value)}
          />
        }
      />
      <FormField
        label="상품회수장소 상세"
        required={form.isDeliveryLocationDetailRequired}
        contents={
          <Textarea
            size="small"
            height="76px"
            placeholder="상품회수장소가 택배 수령실 or 기타 일 경우 필수로 입력해 주세요."
            count
            maxLength={COLLECTING_REQUEST_DETAIL_MAX_LENGTH}
            value={values.deliveryLocationDetail}
            status={errors.deliveryLocationDetail ? 'error' : 'default'}
            validationMessage={errors.deliveryLocationDetail}
            onChange={(event) => form.handleChangeDetailText('deliveryLocationDetail', event.target.value)}
          />
        }
      />
      <FormField
        label="출입방법"
        required
        contentAlign="center"
        contents={
          <Select
            size="small"
            placeholder="선택"
            options={options.entranceMethods}
            value={findOption(options.entranceMethods, values.entranceMethod)}
            status={errors.entranceMethod ? 'error' : 'default'}
            validationMessage={errors.entranceMethod}
            onChange={(_: unknown, option: TypeSelectOption<string>) => form.handleChangeEntranceMethod(option.value)}
          />
        }
      />
      <FormField
        label="출입방법 상세"
        required={form.isEntranceMethodDetailRequired}
        contents={
          <Textarea
            size="small"
            height="76px"
            placeholder="출입 방법이 '공동현관 비밀번호' or '기타'일 경우 필수로 입력해 주세요."
            count
            maxLength={COLLECTING_REQUEST_DETAIL_MAX_LENGTH}
            value={values.entranceMethodDetail}
            status={errors.entranceMethodDetail ? 'error' : 'default'}
            validationMessage={errors.entranceMethodDetail}
            onChange={(event) => form.handleChangeDetailText('entranceMethodDetail', event.target.value)}
          />
        }
      />
      <FormField
        label="회수할 상품명"
        required
        contentAlign="center"
        contents={
          <Input
            type="text"
            size="small"
            placeholder="회수할 상품명을 입력해주세요."
            count
            maxLength={COLLECTING_REQUEST_PRODUCT_NAME_MAX_LENGTH}
            value={values.productName}
            status={errors.productName ? 'error' : 'default'}
            validationMessage={errors.productName}
            onChange={(event) => form.setValue('productName', event.target.value)}
          />
        }
      />
      <FormField
        label="회수할 박스 개수"
        required
        tooltip={{ description: BOX_COUNT_TOOLTIP }}
        contentAlign="center"
        contents={
          <Select
            ref={handleBoxCountSelectRef}
            size="small"
            placeholder="선택"
            searchable
            dropdownPortalTarget={boxCountDropdownTarget}
            options={COLLECTING_REQUEST_BOX_COUNT_OPTIONS}
            value={findOption(COLLECTING_REQUEST_BOX_COUNT_OPTIONS, values.boxCount)}
            status={errors.boxCount ? 'error' : 'default'}
            validationMessage={errors.boxCount}
            onChange={(_: unknown, option: TypeSelectOption<number>) => form.setValue('boxCount', option.value)}
          />
        }
      />
      <FormField
        label="회수한 상품 처리 방식"
        required
        contentAlign="center"
        contents={
          <RadioGroup
            name="collected-product-handling"
            size="medium"
            value={values.collectedProductHandling ?? ''}
            status={errors.collectedProductHandling ? 'error' : 'default'}
            validationMessage={errors.collectedProductHandling}
            onChange={(event) => form.handleChangeCollectedProductHandling(event.target.value)}
          >
            {options.collectedProductHandlings.map((option) => (
              <span key={option.value} className={styles['radio-with-tooltip']}>
                <Radio value={option.value} label={option.text} />
                {option.value === 'RETURN_TO_SENDER' && (
                  // bottom-right 는 툴팁 오른쪽 끝을 아이콘에 맞춰 왼쪽으로 펼친다. 아이콘이 카드 오른쪽에 있어 오른쪽으로 펼치면 잘린다
                  <Tooltip description={HANDLING_TOOLTIP} placement="bottom-right">
                    <HelpCircleFillIcon size={16} color="gray400" />
                  </Tooltip>
                )}
              </span>
            ))}
          </RadioGroup>
        }
      />
      <FormField
        label="알림톡 즉시 발송"
        required
        contentAlign="center"
        contents={
          <RadioGroup
            name="send-alim-talk-immediately"
            size="medium"
            value={values.sendAlimTalkImmediately === null ? '' : String(values.sendAlimTalkImmediately)}
            status={errors.sendAlimTalkImmediately ? 'error' : 'default'}
            validationMessage={errors.sendAlimTalkImmediately}
            onChange={(event) => form.setValue('sendAlimTalkImmediately', event.target.value === 'true')}
          >
            <Radio value="true" label="Y" />
            <Radio value="false" label="N" />
          </RadioGroup>
        }
      />
    </ModalSection>
  );
};

export default CollectingRequestCollectingInfoForm;
